import {
  Activity,
  BarChart3,
  Boxes,
  Briefcase,
  Coins,
  Crosshair,
  Database,
  FlaskConical,
  GitMerge,
  Globe,
  HeartPulse,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Network,
  Radio,
  Receipt,
  ScrollText,
  Shield,
  TrendingUp,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import type { Permission } from '@/lib/auth/roles';
import { hasPermission } from '@/lib/auth/permissions';
import { SHOW_AUDIT_LOG } from '@/lib/config/nav-flags';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Permission required to SEE this item. Omitted = always visible. */
  perm?: Permission;
}

export interface NavGroup {
  /** null = ungrouped (no heading). */
  label: string | null;
  items: NavItem[];
}

/**
 * Navigation follows the algo lifecycle: run the fleet, allocate it, watch it
 * execute, bound its risk, keep the accounts and data healthy, research the
 * next strategy. Manual trading is a secondary tool, last.
 */
export const NAV: NavGroup[] = [
  {
    label: null,
    items: [
      { label: 'Control center', href: '/control', icon: LayoutDashboard },
      { label: 'Overview', href: '/overview', icon: LayoutGrid },
      { label: 'Strategies', href: '/strategies', icon: TrendingUp, perm: 'strategies:read' },
      { label: 'Allocations', href: '/subscriptions', icon: GitMerge, perm: 'subscriptions:read' },
    ],
  },
  {
    label: 'Execution',
    items: [
      { label: 'Orders', href: '/orders', icon: ListChecks, perm: 'orders:read' },
      { label: 'Trades', href: '/trades', icon: Receipt, perm: 'trades:read' },
      { label: 'Positions', href: '/positions', icon: Coins, perm: 'trades:read' },
      { label: 'P&L', href: '/pnl', icon: BarChart3, perm: 'trades:read' },
    ],
  },
  {
    label: 'Risk',
    items: [{ label: 'Risk rules', href: '/risk', icon: Shield, perm: 'risk:read' }],
  },
  {
    label: 'Accounts',
    items: [
      { label: 'Broker accounts', href: '/broker-accounts', icon: Wallet, perm: 'brokers:read' },
      { label: 'Clients', href: '/clients', icon: Users, perm: 'clients:read' },
      { label: 'Brokers', href: '/brokers', icon: Briefcase, perm: 'brokers:read' },
      { label: 'Broker drivers', href: '/broker-drivers', icon: Network, perm: 'brokers:read' },
    ],
  },
  {
    label: 'Research',
    items: [{ label: 'Backtests', href: '/backtests', icon: FlaskConical, perm: 'strategies:read' }],
  },
  {
    label: 'Market data',
    items: [
      { label: 'Streaming', href: '/streaming', icon: Radio },
      { label: 'Instruments', href: '/instruments', icon: Boxes },
      { label: 'Option chains', href: '/option-chains', icon: Layers },
      { label: 'Watchlists', href: '/watchlists', icon: Activity },
      { label: 'Exchanges', href: '/exchanges', icon: Globe },
      { label: 'Data sources', href: '/data-sources', icon: Database },
    ],
  },
  {
    label: 'Manual',
    items: [{ label: 'Terminal', href: '/trade', icon: Crosshair, perm: 'orders:read' }],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Operators', href: '/admin/operators', icon: Users, perm: 'admin:invite_user' },
      { label: 'Observability', href: '/admin/observability', icon: HeartPulse, perm: 'admin:read' },
      ...(SHOW_AUDIT_LOG
        ? [{ label: 'Audit log', href: '/admin/audit', icon: ScrollText, perm: 'audit:read' as const }]
        : []),
    ],
  },
];

/**
 * Terminal (Kite-style) layout: the trading desk's daily pages sit in the top
 * bar, in this order; every other NAV item lands under "More", keeping its NAV
 * group. Derived from NAV so a page added there shows up in both layouts.
 */
const TERMINAL_PRIMARY_HREFS = [
  '/overview',
  '/trade',
  '/positions',
  '/orders',
  '/trades',
  '/pnl',
  '/option-chains',
  '/strategies',
] as const;

const ALL_ITEMS = NAV.flatMap((g) => g.items);

export const PRIMARY_NAV: NavItem[] = TERMINAL_PRIMARY_HREFS.map((href) => {
  const item = ALL_ITEMS.find((i) => i.href === href);
  if (!item) {
    throw new Error(`nav-config: terminal primary href ${href} is not in NAV`);
  }
  return href === '/option-chains' ? { ...item, label: 'Option chain' } : item;
});

export const MORE_NAV: NavGroup[] = NAV.map((g, gi) => ({
  label: g.label ?? (gi === 0 ? 'Fleet' : null),
  items: g.items.filter((i) => !(TERMINAL_PRIMARY_HREFS as readonly string[]).includes(i.href)),
})).filter((g) => g.items.length > 0);

/** Terminal layout: pages that get the docked watchlist (the Terminal IS a full watchlist). */
export const DOCKED_WATCHLIST_PREFIXES = ['/overview', '/positions', '/orders', '/trades', '/pnl', '/option-chains'];

/** Role gating: while the role is loading (null), show everything — the
 *  backend independently 403s anything the role may not do. */
export function visible(item: NavItem, role: Parameters<typeof hasPermission>[0] | null): boolean {
  return !item.perm || !role || hasPermission(role, item.perm);
}

export function isActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}
